// apps/web/src/admin/AdminCallActivityView.tsx
import { useEffect, useState } from 'react';
import { apiAdmin } from './apiAdmin.js';
import { ExportPdfButton } from './ExportPdfButton.js';
import { reportTimestamp } from './reportWindow.js';
import { CallLogTable, callDuration, type CallLogRow } from './CallLogTable.js';

export function AdminCallActivityView() {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => { void (async () => setData(await apiAdmin.callActivity()))(); }, []);
  if (!data) return <p>Loading…</p>;
  const maxDay = Math.max(1, ...data.dailyCounts.map((d: any) => d.count));
  // `?? []` is deliberate: the deployed API can be older than this bundle, and
  // a missing field must render the empty state rather than throw.
  const calls: CallLogRow[] = data.calls ?? [];
  const totalCalls: number = data.dailyCounts.reduce((s: number, d: any) => s + d.count, 0);

  async function exportPdf() {
    const { generateReportPdf } = await import('./reportPdf.js');
    const now = new Date();
    generateReportPdf({
      title: 'Call Activity Report',
      subtitle: `Last 30 days · Generated ${reportTimestamp(now)}`,
      summary: [
        ['Total calls (30d)', String(totalCalls)],
        ['Average duration', `${Math.floor(data.avgDurationSeconds / 60)}m ${data.avgDurationSeconds % 60}s`],
      ],
      tables: [
        { heading: 'Calls per day', columns: ['Date', 'Calls'], rows: data.dailyCounts.map((d: any) => [d.date, d.count]) },
        {
          heading: 'Recent low ratings (≤ 2★, most recent 50)',
          columns: ['When', 'Session', 'Stars', 'Ratee'],
          rows: data.recentLowRatings.map((r: any) => [
            new Date(r.createdAt).toLocaleString(), r.sessionId, r.stars, `${r.rateeName} (${r.rateeRole})`,
          ]),
        },
        {
          heading: 'Calls',
          columns: ['When', 'Learner', 'Guide', 'Duration', 'Billed', 'Guide earned', 'Guide stars', 'Learner stars'],
          rows: calls.map(c => [
            new Date(c.startedAt).toLocaleString(),
            c.learnerName ?? '—',
            c.guideName ?? '—',
            callDuration(c),
            `${c.billedMinutes} min`,
            `$${c.guideEarnedDollars.toFixed(2)}`,
            c.guideStars ?? '—',
            c.learnerStars ?? '—',
          ]),
        },
      ],
      filename: 'calls-last-30-days.pdf',
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportPdfButton onExport={exportPdf} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded border"><div className="text-xs text-ink-500">Avg duration</div><div className="text-2xl font-semibold">{Math.floor(data.avgDurationSeconds/60)}m {data.avgDurationSeconds%60}s</div></div>
        <div className="p-4 rounded border"><div className="text-xs text-ink-500">Calls last 30d</div><div className="text-2xl font-semibold">{totalCalls}</div></div>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Calls per day (last 30 days)</h2>
        <svg viewBox={`0 0 ${data.dailyCounts.length * 12} 60`} className="w-full h-20">
          {data.dailyCounts.map((d: any, i: number) => (
            <rect key={d.date} x={i * 12} y={60 - (d.count / maxDay) * 60} width={10} height={(d.count / maxDay) * 60} className="fill-signwow" />
          ))}
        </svg>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Ratings histogram</h2>
        <div className="flex gap-4">
          {data.ratingsHistogram.map((r: any) => (
            <div key={r.stars} className="text-center">
              <div className="text-2xl font-semibold">{r.count}</div>
              <div className="text-xs text-ink-500">{r.stars}★</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Recent low ratings (≤ 2★, most recent 50)</h2>
        <table className="w-full text-xs">
          <thead className="text-left text-ink-500"><tr><th>When</th><th>Session</th><th>Stars</th><th>Ratee</th></tr></thead>
          <tbody>
            {data.recentLowRatings.map((r: any) => (
              <tr key={r.id}><td>{new Date(r.createdAt).toLocaleString()}</td><td>{r.sessionId}</td><td>{r.stars}</td><td>{r.rateeName} ({r.rateeRole})</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Calls (last 30 days)</h2>
        <CallLogTable calls={calls} totalInWindow={totalCalls} />
      </div>
    </div>
  );
}
